/**
 * BL-1528 — validation for the unified staff-internal notes log.
 *
 * ONE declaration per shape, consumed on BOTH sides (ADR 016 — Form Validation):
 * the server calls `parseBody` / `validateParsed` with these schemas so a
 * rejection comes back as `fieldErrors`, and BL-1529's panel feeds the same
 * `InternalNoteBodySchema` to RHF + `zodResolver` so the inline error under the
 * add box and the server's 400 are literally the same rule.
 *
 * App-scoped in `core-admin` rather than `core`: internal notes are an admin
 * concept. The bidder app does not read or write them and is not planned to.
 */
import { z } from '@auctogon/core/zod';
import { stripNulCharacters } from '@auctogon/core/validation/input-guards';

import type { InternalNoteEntityType } from './responses/internal-note';
import { OfflineOpIdSchema } from './offline-op';

/**
 * The five record types a note can hang off.
 *
 * The `internal_note_entity_type` Postgres enum's values. Adding a member is
 * a short change — an `ALTER TYPE … ADD VALUE` migration, this array, and a
 * row in each of the note grant maps (`NOTE_READ` / `NOTE_WRITE`) and entity
 * maps in the API registry's `internal_notes.ts`, whose entity's table must be a `TENANT_SOURCES` key
 * (BL-1833 PR 6b) — which is the whole reason the table is polymorphic (ADR
 * 052 — Internal Notes). `auction` was the first addition after the initial
 * four (BL-1668). BL-2016 retired `invoice` and `settlement` with the old money
 * tables, and BL-1972 dropped both values from the enum. BL-2026 restored
 * `invoice` for the rebuilt invoices (MN11, Michael Lawrie 2026-09-28), and
 * BL-2525 restored `settlement` for the rebuilt settlements (BL-2508).
 */
export const INTERNAL_NOTE_ENTITY_TYPES = [
  'customer',
  'lot',
  'auction',
  'invoice',
  'settlement',
] as const;

export const InternalNoteEntityTypeSchema = z.enum(INTERNAL_NOTE_ENTITY_TYPES);

/**
 * Narrow a stored `internal_note_entity_type` to a served entity type (BL-2016).
 * Every enum value is served again since BL-2525; the guard stays so a value a
 * later migration adds is refused until this list names it.
 *
 * @param value - The row's stored entity type.
 * @returns Whether a note of this type is still served.
 */
export function isInternalNoteEntityType(value: string): value is InternalNoteEntityType {
  return (INTERNAL_NOTE_ENTITY_TYPES as readonly string[]).includes(value);
}

/**
 * The `code` on DELETE /api/internal-notes/[noteId]'s 404 for a note already deleted in the
 * caller's own tenant (BUG-2060). The offline change queue reads it as "this delete already
 * landed". Every other 404 there (a foreign, unknown or retired-type note) is the gate's, with no code.
 */
export const NOTE_ALREADY_DELETED_CODE = 'NOTE_ALREADY_DELETED';

/** Product cap, matching the `internal_notes_body_max_length` CHECK. */
export const INTERNAL_NOTE_BODY_MAX_LENGTH = 4000;

/**
 * Does the text hold at least one character a reader can see? (BUG-2253.) JS
 * `trim()` and the `btrim` CHECK keep zero-width characters (U+200B, U+200D and the
 * rest of `\p{Cf}`), so a note of only those saved as a blank-looking note. One
 * helper, so the composer's Add button and the server schema agree. A zero-width
 * joiner inside visible text (an emoji sequence) is fine: one visible character is enough.
 *
 * @param text - The note text as typed.
 * @returns `true` when some character is neither whitespace nor a format character.
 */
export function hasVisibleNoteText(text: string): boolean {
  return /[^\s\p{Cf}]/u.test(text);
}

/**
 * The note text.
 *
 * `.trim()` runs BEFORE `.min(1)`, so an all-whitespace body is rejected rather
 * than stored — the same rule as the `internal_notes_body_not_blank` CHECK,
 * enforced here first so the operator gets an inline field error instead of a
 * 500 from a constraint violation. The trimmed value is what gets written.
 */
export const InternalNoteBodyTextSchema = z
  .string()
  // BL-2481 — NUL first: Postgres refuses it in any text value (22021), so an
  // un-stripped paste was a 500 on every note write, offline replays included.
  .overwrite(stripNulCharacters)
  .trim()
  .min(1, 'Note cannot be empty')
  // BUG-2253 — an empty body already failed `min`; this catches the invisible-only ones.
  .refine((value) => value.length === 0 || hasVisibleNoteText(value), 'Note cannot be empty')
  .max(
    INTERNAL_NOTE_BODY_MAX_LENGTH,
    `Note cannot exceed ${INTERNAL_NOTE_BODY_MAX_LENGTH.toLocaleString()} characters`,
  );

export const InternalNoteBodySchema = z.object({
  body: InternalNoteBodyTextSchema,
});

/**
 * Upper bound on notes attached in one create — a guard against a hand-built
 * payload, not a product limit. The composer has no cap of its own.
 */
export const MAX_INTERNAL_NOTES_AT_CREATE = 50;

/**
 * The buffered notes carried on a lot create payload, oldest first.
 *
 * Built from `InternalNoteBodyTextSchema` rather than restating the rule, so a
 * note typed on the Add form and a note typed into the live panel are validated
 * by literally the same declaration — the drift BL-1529 exists to remove. The
 * author is deliberately NOT on this shape: it is stamped server-side from the
 * session, the same way the live `POST /api/internal-notes` does it.
 */
export const InternalNoteBodyArraySchema = z
  .array(InternalNoteBodyTextSchema)
  .max(MAX_INTERNAL_NOTES_AT_CREATE);

/** `POST /api/internal-notes` body. */
export const CreateInternalNoteSchema = InternalNoteBodySchema.extend({
  entityType: InternalNoteEntityTypeSchema,
  entityId: z.string().uuid('entityId must be a UUID'),
  /** BL-2337 — an offline create's op id: a retry answers with the note it made. */
  clientOpId: OfflineOpIdSchema.optional(),
});

/**
 * `PATCH /api/internal-notes/:noteId` body.
 *
 * Body only — a note can never be moved to a different record, so `entityType`
 * and `entityId` are deliberately not patchable.
 */
export const UpdateInternalNoteSchema = InternalNoteBodySchema;

/**
 * `GET /api/internal-notes` query string.
 *
 * Validated with the same declaration as the bodies rather than hand-parsed off
 * `searchParams`, so a malformed `entityId` is a 400 with `fieldErrors` like
 * every other bad input instead of an empty list that looks like "no notes".
 *
 * `includeDeleted` accepts the `1` / `true` the super-admin support read sends;
 * anything else — including its absence — means false. A non-super-admin asking
 * for it is ignored, not rejected: the flag is a widening a normal operator
 * simply does not get, and 403-ing an otherwise valid read would be noise.
 */
export const InternalNoteListQuerySchema = z.object({
  entityType: InternalNoteEntityTypeSchema,
  entityId: z.string().uuid('entityId must be a UUID'),
  includeDeleted: z
    .enum(['1', 'true'])
    .optional()
    .transform((v) => v !== undefined),
});

export type InternalNoteBodyInput = z.infer<typeof InternalNoteBodySchema>;
export type CreateInternalNoteInput = z.infer<typeof CreateInternalNoteSchema>;
export type UpdateInternalNoteInput = z.infer<typeof UpdateInternalNoteSchema>;
export type InternalNoteListQuery = z.infer<typeof InternalNoteListQuerySchema>;
